import React, { useState } from 'react';

import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Shimmer, Table, Text } from '@betterment/samba-react';
import { SystemStyleObject } from '@betterment/samba-styled-system/types';
import { range } from 'es-toolkit/compat';

import { Paginator } from '../../../paginator/Paginator';
import {
  BillingPlanAffectedHouseholdsDocument,
  BillingPlanAffectedHouseholdsQuery,
} from '../graphql/query.billing_plan_affected_households.generated';

type AffectedHousehold = BillingPlanAffectedHouseholdsQuery['billingPlanAffectedHouseholds'][number];

const Rows: React.FC<{ households: AffectedHousehold[] }> = ({ households }) => {
  return households.flatMap(household => {
    const goalRows: React.JSX.Element[] = household.openGoals.map((goal, index) => (
      <Table.Row key={goal.id} css={{ borderBottom: 'none' }}>
        {index === 0 && (
          <Table.Cell rowSpan={household.openGoals.length} css={{ py: 'space.300', verticalAlign: 'top' }}>
            {household.name}
          </Table.Cell>
        )}
        <Table.Cell css={{ py: 'space.300' }}>
          {goal.name}
          <br />
          <Text color="text.weak">{goal.legalAccountType}</Text>
        </Table.Cell>
        <Table.Cell css={{ py: 'space.300' }} alignment="end">
          {formatMoney(goal.currentBalance)}
        </Table.Cell>
      </Table.Row>
    ));

    // Add a fake row as a visual separator
    const separatorRow = <Table.Row key={`separator-${household.id}`} />;

    return [...goalRows, separatorRow];
  });
};

const AffectedHouseholdsTable: React.FC<{ billingPlanId: string; numberOfClients: number }> = ({
  billingPlanId,
  numberOfClients,
}) => {
  const PAGE_SIZE = 10;

  const numberOfPages = Math.ceil(numberOfClients / PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const offset = (currentPage - 1) * PAGE_SIZE;
  const { data, loading } = useGraphQLQuery(BillingPlanAffectedHouseholdsDocument, {
    variables: { billingPlanId, offset },
  });

  const borderStyles: SystemStyleObject = {
    borderWidth: 'thin',
    borderStyle: 'solid',
    borderColor: 'border',
    borderRadius: 'medium',
  };

  return (
    <>
      <Box css={{ ...borderStyles }}>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell css={{ width: '37%' }}>Client</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell css={{ width: '37%' }}>Account(s)</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell css={{ width: '26%' }} alignment="end">
                Balance
              </Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {loading || data === undefined ? (
              range(PAGE_SIZE).map(row => (
                <Table.Row key={row}>
                  {range(3).map((index, _, array) => {
                    const isLast = index === array.length - 1;
                    return (
                      <Table.Cell key={`${row}-${index}`} alignment={isLast ? 'end' : undefined}>
                        <Shimmer
                          css={{
                            height: '12px',
                            width: '70%',
                            ...(isLast && { marginLeft: 'auto' }),
                          }}
                        />
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              ))
            ) : (
              <Rows households={data.billingPlanAffectedHouseholds} />
            )}
          </Table.Body>
        </Table>
      </Box>
      {numberOfClients > PAGE_SIZE && (
        <Paginator
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          numberOfPages={numberOfPages}
          css={{ mt: 'space.300' }}
        />
      )}
    </>
  );
};

export default AffectedHouseholdsTable;
